import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/data-service';

interface BootstrapGateProps {
  children: React.ReactNode;
}

type BootstrapState = 'loading' | 'ready' | 'error';

interface AuthenticatedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department?: string;
  photo?: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export function BootstrapGate({ children }: BootstrapGateProps) {
  const { isProduction, setAuthenticatedUser } = useAuth();
  
  const [state, setState] = useState<BootstrapState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  const bootstrap = useCallback(async (isRetry = false) => {
    if (!isProduction) {
      setState('ready');
      return;
    }

    if (!isRetry) {
      setState('loading');
      setErrorMessage('');
    }
    setIsRetrying(isRetry);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await authFetch(`${baseUrl}/employees/me`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('[BootstrapGate] Employee not found for authenticated user, continuing without employee data');
          setHasBootstrapped(true);
          setRetryCount(0);
          setState('ready');
          return;
        }
        throw new Error(`Failed to fetch employee: ${response.status} ${response.statusText}`);
      }

      const employee: AuthenticatedUser = await response.json();
      
      if (employee && employee.id) {
        setAuthenticatedUser(employee);
        console.log(`[BootstrapGate] Successfully loaded employee: ${employee.name} (${employee.id})`);
        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
      } else {
        throw new Error('Invalid employee data received');
      }
    } catch (error) {
      console.error('[BootstrapGate] Failed to fetch employee:', error);
      
      // If we get a JSON parse error (HTML response), it means backend is not available
      // Just continue without employee data rather than blocking the app
      if (error instanceof SyntaxError || (error instanceof Error && error.message.includes('JSON'))) {
        console.warn('[BootstrapGate] Backend not available, continuing without employee data');
        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
        return;
      }
      
      const currentRetryCount = isRetry ? retryCount + 1 : 1;
      setRetryCount(currentRetryCount);
      
      if (currentRetryCount < MAX_RETRIES) {
        console.log(`[BootstrapGate] Retrying in ${RETRY_DELAY_MS}ms (attempt ${currentRetryCount}/${MAX_RETRIES})`);
        setTimeout(() => {
          bootstrap(true);
        }, RETRY_DELAY_MS * Math.pow(2, currentRetryCount - 1));
      } else {
        // After max retries, just continue without employee data
        console.warn('[BootstrapGate] Max retries reached, continuing without employee data');
        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
      }
    }
  }, [isProduction, setAuthenticatedUser, retryCount]);

  const handleRetry = useCallback(() => {
    setRetryCount(0);
    bootstrap(false);
  }, [bootstrap]);

  useEffect(() => {
    if (!hasBootstrapped && isProduction) {
      bootstrap(false);
    } else if (!isProduction) {
      setState('ready');
    }
  }, [isProduction, hasBootstrapped, bootstrap]);

  if (!isProduction) {
    return <>{children}</>;
  }

  if (state === 'loading' || isRetrying) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Setting up your workspace...</p>
          <p className="text-sm text-muted-foreground mt-1">Authenticating and loading your data...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <h2 className="text-xl font-semibold text-destructive mb-2">Unable to Load Application</h2>
          <p className="text-muted-foreground mb-4">
            {errorMessage || 'We encountered an error while loading your data. Please try again.'}
          </p>
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
